import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { ShieldOff, ShieldCheck } from 'lucide-react'
import { isOptedOut, setOptOut } from '@/lib/optout'
import { siteBrand } from '@/lib/site'

export const Route = createFileRoute('/privacy/opt-out')({
    component: PrivacyOptOutPage,
})

function PrivacyOptOutPage() {
    const [out, setOut] = useState(false)
    const [hydrated, setHydrated] = useState(false)

    useEffect(() => {
        setOut(isOptedOut())
        setHydrated(true)
    }, [])

    function handleOptOut() {
        setOptOut(true)
        setOut(true)
    }

    function handleOptIn() {
        setOptOut(false)
        setOut(false)
    }

    return (
        <div className="bg-white">
            <div className="mx-auto max-w-2xl px-6 pb-20 pt-12">
                <p className="text-sm font-medium text-slate-500">Privacy</p>
                <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
                    Do Not Sell or Share My Personal Information
                </h1>
                <p className="mt-3 text-base text-slate-600">
                    {siteBrand().name} doesn't sell your personal information. We do share anonymized
                    engagement and feedback with the organizations that sponsor our content. You
                    can opt out of all of this on this device with one click.
                </p>

                <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-6">
                    {!hydrated ? (
                        <p className="text-sm text-slate-500">Loading…</p>
                    ) : out ? (
                        <>
                            <div className="flex items-center gap-2 text-emerald-700">
                                <ShieldCheck className="h-5 w-5" />
                                <span className="font-semibold">You're opted out on this device.</span>
                            </div>
                            <p className="mt-2 text-sm text-slate-600">
                                Google Analytics is disabled, course events aren't recorded, and you
                                can't submit feedback. Your existing data has not been deleted by
                                this action. To request deletion, email{' '}
                                <a href="mailto:privacy@scamdefense.org" className="underline">
                                    privacy@scamdefense.org
                                </a>
                                .
                            </p>
                            <button
                                type="button"
                                onClick={handleOptIn}
                                className="mt-5 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 cursor-pointer"
                            >
                                Re-enable analytics & feedback
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="flex items-center gap-2 text-slate-900">
                                <ShieldOff className="h-5 w-5" />
                                <span className="font-semibold">Opt out on this device</span>
                            </div>
                            <p className="mt-2 text-sm text-slate-600">
                                We will stop loading Google Analytics, recording course progress
                                events, and accepting feedback from this browser. Other devices
                                you use will need to opt out separately.
                            </p>
                            <button
                                type="button"
                                onClick={handleOptOut}
                                className="mt-5 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 cursor-pointer"
                            >
                                Opt out
                            </button>
                        </>
                    )}
                </div>

                <p className="mt-6 text-xs text-slate-400">
                    The opt-out is stored in a first-party cookie called <code>sd_optout</code> and
                    lasts one year. Clearing your cookies will undo the opt-out.
                </p>
            </div>
        </div>
    )
}
